import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { ADDITIONAL_DATA_MARKING_TYPE_NON_CONDITIONAL } from "abreact/expression/ui/editor/impl/GuidedNonBooleanExpressionEditor";
import { IfThenElseModel } from "abreact/expression/ui/editor/impl/model/impl/conditional/IfThenElseModel";
import { SummaryElement, SummaryElementType } from "abreact/expression/ui/editor/readonly/ExpressionSummaryElement";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { ExpExpressionNodeNodeTypeEnum } from "abweb/generated/abwebinternal/api/dto/ExpExpressionNode";
export class IfThenElseCustomOperandContributor {
  get identifier() {
    return IfThenElseCustomOperandContributor.IDENTIFIER;
  }
  appliesToNode(node) {
    return this.isIfThenElseFunction(node) || this.isNonConditionalValue(node);
  }
  isIfThenElseFunction(node) {
    return node && node.additionalData != null && node.additionalData.type === IfThenElseModel.ADDITIONAL_DATA_MARKING_TYPE;
  }
  isNonConditionalValue(node) {
    return node && node.additionalData != null && node.additionalData.type === ADDITIONAL_DATA_MARKING_TYPE_NON_CONDITIONAL;
  }
  contributeOperator(model, expExpressionNode, summaryMode, structureNode) {
    if (this.isIfThenElseFunction(expExpressionNode)) {
      this.contributeIfThenElseOperator(model, expExpressionNode, summaryMode, structureNode);
    } else if (this.isNonConditionalValue(expExpressionNode)) {
      this.contributeValueOperator(model, expExpressionNode, summaryMode, structureNode);
    }
  }
  contributeValueOperator(model, expExpressionNode, summaryMode, structureNode) {
    const valueOperand = expExpressionNode.operandValues?.find(op => op.operandName === "value");
    if (valueOperand) {
      const valueLabel = expExpressionNode?.additionalData?.values["valueLabel"];
      if (valueLabel) {
        model.currentRow.addElement(new SummaryElement(SummaryElementType.TEXT, valueLabel));
      }
      if (valueOperand.nodeType === ExpExpressionNodeNodeTypeEnum.OPERATOR) {
        structureNode.contributeOperator(model, valueOperand, summaryMode, valueOperand.operatorID);
      } else {
        model.currentRow.addElement(new SummaryElement(SummaryElementType.LEAF, valueOperand));
      }
    } else {
      structureNode.contributeOperator(model, expExpressionNode, summaryMode);
    }
  }
  contributeIfThenElseOperator(model, expExpressionNode, summaryMode, structureNode) {
    let currentExpressionNode = expExpressionNode;
    let expExpressionNodes = [];
    model.customSubExpressionDepthAmplification = 1;
    while (currentExpressionNode) {
      expExpressionNodes.push(currentExpressionNode);
      let elseNode = this.getElseNode(currentExpressionNode);
      /*
      At the end of this chain will be an else node that is not another if_then_else function but something else
      like a literal, etc. Don't include that one.
       */
      if (elseNode?.operatorID === currentExpressionNode.operatorID) {
        currentExpressionNode = elseNode;
      } else {
        currentExpressionNode = undefined;
      }
    }
    let counter = 0;
    for (let ifThenElseNode of expExpressionNodes) {
      const isLastOne = counter === expExpressionNodes.length - 1;
      const isFirstOne = counter === 0;
      let ifNode = this.findNode("if", ifThenElseNode);
      let thenNode = this.findNode("then", ifThenElseNode);
      let elseNode = this.findNode("else", ifThenElseNode);
      if (!isFirstOne) {
        model.startNewRow();
      }
      model.currentRow.depth = 0;
      if (ifNode) {
        this.addNodeToModel(ifNode, model, summaryMode, structureNode, isFirstOne ? AbReactResources.nonboolean_editor_if_label() : AbReactResources.nonboolean_editor_elseIf_label());
      }
      if (thenNode) {
        model.startNewRow();
        model.currentRow.depth = 2;
        this.addNodeToModel(thenNode, model, summaryMode, structureNode, AbReactResources.nonboolean_editor_then_label());
      }
      if (isLastOne && elseNode) {
        model.startNewRow();
        model.currentRow.depth = 2;
        this.addNodeToModel(elseNode, model, summaryMode, structureNode, AbReactResources.nonboolean_editor_else_label());
      }
      counter++;
    }
  }
  addNodeToModel(value, model, summaryMode, structureNode, rowLabel) {
    if (value) {
      model.currentRow.addElement(new SummaryElement(SummaryElementType.TEXT, rowLabel, {
        cssClassName: "ab-gee-nonboolean-summary-condition-label"
      }));
      if (value.nodeType === ExpExpressionNodeNodeTypeEnum.OPERATOR) {
        if (value.categoryID === Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION) {
          model.customSubExpressionDepthAmplification = 2;
          structureNode.contributeSubExpression(model, value, summaryMode, structureNode.value.operatorID);
        } else {
          structureNode.contributeOperator(model, value, summaryMode);
        }
      } else {
        model.currentRow.addElement(new SummaryElement(SummaryElementType.LEAF, value));
      }
    }
  }
  getElseNode(expExpressionNode) {
    return this.findNode("else", expExpressionNode);
  }
  findNode(operandName, expExpressionNode) {
    return expExpressionNode.operandValues?.find(op => op.operandName === operandName);
  }
}
_defineProperty(IfThenElseCustomOperandContributor, "IDENTIFIER", "if_then_else_function_renderer");
//# sourceMappingURL=IfThenElseCustomOperandContributor.js.map
